import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'

import {
  listInspectorsPositions,
  randomizeAllInspectorPositions,
  randomizeAllInspectorTimes,
  refreshRealInspectorAddresses,
  setInspectorPositionSimulated,
  type InspectorPositionRow,
  type RandomizeResult,
  type RefreshAddressesResult,
} from '@/api/inspectors_positions'
import { listSystemConfig } from '@/api/system_config'
import { getGeoSettings, GEO_SETTINGS_DEFAULTS } from '@/api/geo_settings'
import { useInspectorsPositionsRealtime } from '@/hooks/useInspectorsPositionsRealtime'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useAuthStore } from '@/store/authStore'
import { PageHeader } from '@/components/layout/PageHeader'
import { SearchInput } from '@/components/ui/SearchInput'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { EditInspectorPositionModal } from '@/components/inspectors/EditInspectorPositionModal'
import { formatPhoneDisplay } from '@/utils/phone'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { emptyTableCellClassName } from '@/constants/ui'
import i18n from '@/utils/i18n'
import { dateFnsLocale } from '@/utils/dateLocale'

const PAGE_SIZE = 50
const STALE_MINUTES = 60

type Freshness = 'fresh' | 'recent' | 'old' | 'missing'

function freshness(
  iso: string | null,
  freshMinutes: number,
): Freshness {
  if (!iso) return 'missing'
  const ageMin = (Date.now() - new Date(iso).getTime()) / 60_000
  if (ageMin <= freshMinutes) return 'fresh'
  if (ageMin <= STALE_MINUTES) return 'recent'
  return 'old'
}

/** Состояние «связи» между админкой и устройством инспектора.
 *
 * Источник — `inspector_keepalive.last_keepalive_at`. В отличие от свежести
 * позиции (которая стареет, когда инспектор стоит на месте), keepalive идёт
 * по таймеру независимо от GPS — позволяет отличать «стоит» от «глухо». */
type Connection = 'online' | 'lagging' | 'lost' | 'unknown'

function connection(
  iso: string | null,
  graceSeconds: number,
): Connection {
  if (!iso) return 'unknown'
  const ageSec = (Date.now() - new Date(iso).getTime()) / 1000
  if (ageSec <= graceSeconds) return 'online'
  if (ageSec <= graceSeconds * 3) return 'lagging'
  return 'lost'
}

/** Pill-бейдж в стиле CompanyStatusBadge для колонки «GPS-точка».
 *  label/tip — i18n-ключи, резолвятся через i18n.t в момент рендера. */
const FRESH_BADGE: Record<Freshness, { labelKey: string; cls: string; tipKey: string }> = {
  fresh: {
    labelKey: 'geo.fresh.fresh.label',
    cls: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
    tipKey: 'geo.fresh.fresh.tip',
  },
  recent: {
    labelKey: 'geo.fresh.recent.label',
    cls: 'border-yellow-500/30 bg-yellow-500/12 text-yellow-300',
    tipKey: 'geo.fresh.recent.tip',
  },
  old: {
    labelKey: 'geo.fresh.old.label',
    cls: 'border-red-500/30 bg-red-500/12 text-red-300',
    tipKey: 'geo.fresh.old.tip',
  },
  missing: {
    labelKey: 'geo.fresh.missing.label',
    cls: 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]',
    tipKey: 'geo.fresh.missing.tip',
  },
}

/** Помощник: добавляет к base-tooltip строку «Обновлено: N минут назад» если iso есть. */
function withTime(base: string, iso: string | null): string {
  if (!iso) return base
  return `${base}\n${i18n.t('geo.positions.tooltipUpdated', {
    time: formatDistanceToNow(new Date(iso), {
      locale: dateFnsLocale(),
      addSuffix: true,
    }),
  })}`
}

const CONN_BADGE: Record<Connection, { labelKey: string; cls: string; tipKey: string }> = {
  online: {
    labelKey: 'geo.conn.online.label',
    cls: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
    tipKey: 'geo.conn.online.tip',
  },
  lagging: {
    labelKey: 'geo.conn.lagging.label',
    cls: 'border-yellow-500/30 bg-yellow-500/12 text-yellow-300',
    tipKey: 'geo.conn.lagging.tip',
  },
  lost: {
    labelKey: 'geo.conn.lost.label',
    cls: 'border-red-500/30 bg-red-500/12 text-red-300',
    tipKey: 'geo.conn.lost.tip',
  },
  unknown: {
    labelKey: 'geo.conn.unknown.label',
    cls: 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]',
    tipKey: 'geo.conn.unknown.tip',
  },
}

const FRESH_PILL_BASE =
  'inline-flex shrink-0 items-center rounded-[100px] border px-2 py-0.5 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] font-medium uppercase tracking-[0.5px]'

type SortKey = 'fresh' | 'full_name' | 'last_position_at'
type SortDir = 'asc' | 'desc'

export function InspectorsPositionsPage() {
  const { t } = useTranslation()
  const profile = useAuthStore((s) => s.profile)
  const isAdmin = profile?.role === 'admin'
  const qc = useQueryClient()

  const [search, setSearch] = useState('')
  const [freshOnly, setFreshOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [sortKey, setSortKey] = useState<SortKey>('fresh')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [editing, setEditing] = useState<InspectorPositionRow | null>(null)
  const [confirmRandomize, setConfirmRandomize] = useState(false)
  const [confirmRandomizeTimes, setConfirmRandomizeTimes] = useState(false)
  const [confirmRefresh, setConfirmRefresh] = useState(false)
  const [randomizeMsg, setRandomizeMsg] = useState<{
    kind: 'success' | 'error'
    text: string
  } | null>(null)

  const debouncedSearch = useDebouncedValue(search, 250)

  const NO_TEST_MSG = t('geo.positions.noTestMsg')

  const randomizeMut = useMutation({
    mutationFn: randomizeAllInspectorPositions,
    onSuccess: (r: RandomizeResult) => {
      setConfirmRandomize(false)
      setRandomizeMsg({
        kind: 'success',
        text:
          r.total === 0
            ? NO_TEST_MSG
            : t('geo.positions.updatedCount', {
                updated: r.updated,
                total: r.total,
                failed: r.failed,
              }),
      })
      void qc.invalidateQueries({ queryKey: ['inspectors-positions'] })
      void qc.invalidateQueries({ queryKey: ['dispatch-map'] })
    },
    onError: (e: Error) => {
      setRandomizeMsg({
        kind: 'error',
        text: t('geo.positions.errorPrefix', { message: e.message }),
      })
    },
  })

  const refreshAddressesMut = useMutation({
    mutationFn: refreshRealInspectorAddresses,
    onSuccess: (r: RefreshAddressesResult) => {
      setConfirmRefresh(false)
      setRandomizeMsg({
        kind: 'success',
        text:
          r.total === 0
            ? t('geo.positions.noRealToRefresh')
            : t('geo.positions.addressesUpdatedCount', {
                updated: r.updated,
                total: r.total,
                skipped: r.skipped,
                failed: r.failed,
              }),
      })
      void qc.invalidateQueries({ queryKey: ['inspectors-positions'] })
      void qc.invalidateQueries({ queryKey: ['dispatch-map'] })
    },
    onError: (e: Error) => {
      setRandomizeMsg({
        kind: 'error',
        text: t('geo.positions.errorPrefix', { message: e.message }),
      })
    },
  })

  const randomizeTimesMut = useMutation({
    mutationFn: randomizeAllInspectorTimes,
    onSuccess: (r: RandomizeResult) => {
      setConfirmRandomizeTimes(false)
      setRandomizeMsg({
        kind: 'success',
        text:
          r.total === 0
            ? NO_TEST_MSG
            : t('geo.positions.timesUpdatedCount', {
                updated: r.updated,
                total: r.total,
                failed: r.failed,
              }),
      })
      void qc.invalidateQueries({ queryKey: ['inspectors-positions'] })
      void qc.invalidateQueries({ queryKey: ['dispatch-map'] })
    },
    onError: (e: Error) => {
      setRandomizeMsg({
        kind: 'error',
        text: t('geo.positions.errorPrefix', { message: e.message }),
      })
    },
  })

  // Переключение тестового режима (защита виртуальных позиций от GPS-стрима).
  const simulatedMut = useMutation({
    mutationFn: ({ id, simulated }: { id: string; simulated: boolean }) =>
      setInspectorPositionSimulated(id, simulated),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inspectors-positions'] })
    },
    onError: (e: Error) => {
      setRandomizeMsg({
        kind: 'error',
        text: t('geo.positions.toggleTestFailed', { message: e.message }),
      })
    },
  })

  // Конфиг свежести из system_config (фолбэк 5).
  const { data: cfg } = useQuery({
    queryKey: ['system-config'],
    queryFn: listSystemConfig,
    staleTime: 60_000,
  })
  const freshMinutes = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'position_fresh_minutes')?.value
    const n = v ? Number(v) : 5
    return Number.isFinite(n) && n > 0 ? n : 5
  }, [cfg])

  // Live-обновления через realtime → invalidate кэша.
  useInspectorsPositionsRealtime()

  // Geo-settings — нужны пороги silent_grace_s для чипа «Пульс».
  // staleTime большой, query фоновая: значения меняются редко.
  const { data: geo } = useQuery({
    queryKey: ['geo-settings'],
    queryFn: getGeoSettings,
    staleTime: 5 * 60_000,
  })
  const activeGraceS =
    geo?.active_silent_grace_s ?? GEO_SETTINGS_DEFAULTS.active_silent_grace_s ?? 240
  const idleGraceS =
    geo?.idle_silent_grace_s ?? GEO_SETTINGS_DEFAULTS.idle_silent_grace_s ?? 900

  // Tick раз в 15с — заставляет перерисовать чипы (свежесть и связь стареют
  // независимо от refetch'а). Сами данные обновляются через realtime сабскрайб
  // на profiles + refetchInterval (см. ниже).
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15_000)
    return () => clearInterval(id)
  }, [])

  const { data, isFetching, error } = useQuery({
    queryKey: [
      'inspectors-positions',
      debouncedSearch,
      freshOnly,
      freshMinutes,
      page,
    ],
    queryFn: () =>
      listInspectorsPositions({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        freshOnly,
        freshMinutes,
      }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // keepalive не публикуется в supabase_realtime (см. миграцию
    // 20260511150000_inspector_keepalive.sql), поэтому здесь периодически
    // дотягиваем свежие last_keepalive_at. 30с — копромисс между нагрузкой
    // и реактивностью индикатора «Пульс».
    refetchInterval: 30_000,
  })

  const sortedRows = useMemo(() => {
    const rows = data?.rows ?? []
    if (sortKey === 'fresh') {
      // По свежести: fresh < recent < old < missing (для desc — обратный порядок).
      const order: Record<Freshness, number> = { fresh: 0, recent: 1, old: 2, missing: 3 }
      const dirMul = sortDir === 'asc' ? 1 : -1
      return [...rows].sort((a, b) => {
        const fa = freshness(a.last_position_at, freshMinutes)
        const fb = freshness(b.last_position_at, freshMinutes)
        // Стабильный вторичный ключ — last_position_at desc.
        if (fa === fb) {
          const ta = a.last_position_at ? new Date(a.last_position_at).getTime() : 0
          const tb = b.last_position_at ? new Date(b.last_position_at).getTime() : 0
          return tb - ta
        }
        // Для свежести «desc» = «свежие сверху», т.е. fresh < recent → отрицательно
        // для свежих. Меняем знак при asc.
        return (order[fa] - order[fb]) * -dirMul
      })
    }
    if (sortKey === 'last_position_at') {
      const dirMul = sortDir === 'asc' ? 1 : -1
      return [...rows].sort((a, b) => {
        const ta = a.last_position_at ? new Date(a.last_position_at).getTime() : 0
        const tb = b.last_position_at ? new Date(b.last_position_at).getTime() : 0
        return (ta - tb) * dirMul
      })
    }
    // Текстовые
    const dirMul = sortDir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = (a[sortKey] ?? '') as string
      const vb = (b[sortKey] ?? '') as string
      return va.localeCompare(vb, 'ru') * dirMul
    })
  }, [data?.rows, sortKey, sortDir, freshMinutes])

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'fresh' || key === 'last_position_at' ? 'desc' : 'asc')
    }
  }

  // total — estimated (count: 'estimated' в API ради скорости). На малом
  // числе строк PostgreSQL может вернуть 0 → подстраховываемся длиной страницы.
  const total = Math.max(data?.total ?? 0, data?.rows.length ?? 0)
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  /** Выбор порога grace по текущему режиму трекера: для active_order шкала
   *  жёстче (по умолчанию 240с), для idle/off — мягче (900с). */
  const graceFor = (row: InspectorPositionRow): number =>
    row.tracking_mode === 'active_order' ? activeGraceS : idleGraceS

  return (
    <div>
      <PageHeader
        title={t('geo.positions.title')}
        actions={
          isAdmin ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setRandomizeMsg(null)
                  setConfirmRefresh(true)
                }}
              >
                {t('geo.positions.refreshRealAddresses')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setRandomizeMsg(null)
                  setConfirmRandomize(true)
                }}
              >
                {t('geo.positions.setTestAddresses')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setRandomizeMsg(null)
                  setConfirmRandomizeTimes(true)
                }}
              >
                {t('geo.positions.setTimes')}
              </Button>
            </div>
          ) : null
        }
        filters={
          <>
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder={t('geo.positions.searchPlaceholder')}
            />
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              <input
                type="checkbox"
                checked={freshOnly}
                onChange={(e) => {
                  setFreshOnly(e.target.checked)
                  setPage(1)
                }}
              />
              {t('geo.positions.freshOnly')}
            </label>
          </>
        }
      />

      {randomizeMsg ? (
        <div
          className={
            randomizeMsg.kind === 'success'
              ? 'mb-3 rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200'
              : 'mb-3 rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200'
          }
        >
          {randomizeMsg.text}
        </div>
      ) : null}

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
          {t('geo.positions.errorPrefix', { message: (error as Error).message })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
          {isFetching && data ? <LoadingSecondsBanner /> : null}
          <table className="w-full text-left text-sm">
            <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
              <tr>
                <SortableTh label={t('geo.positions.col.fullName')} k="full_name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th className="px-3 py-2">{t('geo.positions.col.phone')}</th>
                <th className="px-3 py-2">Lat</th>
                <th className="px-3 py-2">Lon</th>
                <th className="px-3 py-2">{t('geo.positions.col.address')}</th>
                <SortableTh label={t('geo.positions.col.updated')} k="last_position_at" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <SortableTh label={t('geo.positions.col.gpsPoint')} k="fresh" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                <th
                  className="px-3 py-2"
                  title={t('geo.positions.pulseTitle')}
                >
                  {t('geo.positions.col.pulse')}
                </th>
                <th
                  className="px-3 py-2"
                  title={t('geo.positions.geotrackingTitle')}
                >
                  {t('geo.positions.col.geotracking')}
                </th>
                <th className="px-3 py-2">{t('geo.positions.col.activeOrder')}</th>
                <th className="px-3 py-2">{t('geo.positions.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!data && isFetching ? (
                <tr>
                  <td colSpan={11} className={emptyTableCellClassName}>
                    <Loader centered size="sm" />
                  </td>
                </tr>
              ) : sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className={emptyTableCellClassName}>
                    {t('common.noData')}
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => {
                  const fresh = freshness(row.last_position_at, freshMinutes)
                  return (
                    <tr
                      key={row.id}
                      className="border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
                    >
                      <td className="px-3 py-2 font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/inspectors/${row.id}/last-position`}
                            className="text-[color:var(--stryzh-color-text-primary)] hover:text-[color:var(--stryzh-color-accent-orange)]"
                            title={t('geo.positions.openTrack')}
                          >
                            {row.full_name ?? '—'}
                          </Link>
                          {row.position_simulated ? (
                            <span
                              title={t('geo.positions.testBadgeTitle')}
                              className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"
                            >
                              TEST
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {formatPhoneDisplay(row.phone)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.last_lat != null ? row.last_lat.toFixed(6) : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {row.last_lon != null ? row.last_lon.toFixed(6) : '—'}
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-[color:var(--stryzh-color-text-secondary)] max-w-[20ch] truncate"
                        title={row.last_address ?? undefined}
                      >
                        {row.last_address ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                        {row.last_position_at
                          ? formatDistanceToNow(new Date(row.last_position_at), {
                              locale: dateFnsLocale(),
                              addSuffix: true,
                            })
                          : t('geo.positions.noData')}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`${FRESH_PILL_BASE} ${FRESH_BADGE[fresh].cls}`}
                          title={withTime(t(FRESH_BADGE[fresh].tipKey), row.last_position_at)}
                        >
                          {t(FRESH_BADGE[fresh].labelKey)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <ConnectionPill row={row} graceSeconds={graceFor(row)} />
                      </td>
                      <td className="px-3 py-2">
                        <TrackerCell row={row} />
                      </td>
                      <td className="px-3 py-2">
                        {row.active_order ? (
                          <a
                            href={`/orders/${row.active_order.id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-[color:var(--stryzh-color-text-primary)] hover:text-[color:var(--stryzh-color-accent-orange)]"
                          >
                            <span className="rounded border border-[color:var(--stryzh-color-border-default)] bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-[color:var(--stryzh-color-text-secondary)]">
                              {row.active_order.id.slice(0, 8)}
                            </span>
                            <span className="text-[color:var(--stryzh-color-text-secondary)]">
                              {row.active_order.status}
                            </span>
                          </a>
                        ) : (
                          <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/inspectors/${row.id}/last-position`}
                            title={t('geo.positions.inspectorTrack')}
                            className="inline-flex h-7 w-7 items-center justify-center rounded border border-[color:var(--stryzh-color-border-default)] text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)] hover:text-[color:var(--stryzh-color-accent-orange)]"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M9 20l-5.447-2.724A2 2 0 0 1 2.5 15.5V4l6 3 7-3 6 3v11.5a2 2 0 0 1-1.053 1.776L15 20l-6-3z" />
                              <line x1="9" y1="3" x2="9" y2="20" />
                              <line x1="15" y1="4" x2="15" y2="17" />
                            </svg>
                          </Link>
                          {row.last_lat != null && row.last_lon != null ? (
                            <IconBtn
                              title={t('geo.positions.copyLatLon')}
                              onClick={() => {
                                void navigator.clipboard.writeText(
                                  `${row.last_lat},${row.last_lon}`,
                                )
                              }}
                            >
                              ⧉
                            </IconBtn>
                          ) : null}
                          {isAdmin ? (
                            <IconBtn
                              title={t('geo.positions.editPosition')}
                              onClick={() => setEditing(row)}
                            >
                              ✎
                            </IconBtn>
                          ) : null}
                          {isAdmin ? (
                            <IconBtn
                              title={
                                row.position_simulated
                                  ? t('geo.positions.disableTestTitle')
                                  : t('geo.positions.enableTestTitle')
                              }
                              onClick={() =>
                                simulatedMut.mutate({
                                  id: row.id,
                                  simulated: !row.position_simulated,
                                })
                              }
                            >
                              {row.position_simulated ? '🧪' : '○'}
                            </IconBtn>
                          ) : null}
                          {row.last_lat != null && row.last_lon != null ? (
                            <IconBtn
                              title={t('geo.positions.openInYandex')}
                              onClick={() => {
                                window.open(
                                  `https://yandex.ru/maps/?pt=${row.last_lon},${row.last_lat}&z=16`,
                                  '_blank',
                                  'noopener,noreferrer',
                                )
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <polygon points="3 11 22 2 13 21 11 13 3 11" />
                              </svg>
                            </IconBtn>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {pageCount > 1 ? (
        <div className="mt-3 flex items-center justify-between text-sm text-[color:var(--stryzh-color-text-secondary)]">
          <div>
            {t('geo.positions.pagination', { page, pageCount, total })}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t('geo.positions.prev')}
            </Button>
            <Button variant="ghost" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
              {t('geo.positions.next')}
            </Button>
          </div>
        </div>
      ) : null}

      {editing && isAdmin ? (
        <EditInspectorPositionModal
          inspectorId={editing.id}
          inspectorName={editing.full_name ?? t('geo.positions.noNamePlaceholder')}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {confirmRefresh && isAdmin ? (
        <Modal
          title={t('geo.positions.refreshModalTitle')}
          onClose={() => {
            if (!refreshAddressesMut.isPending) setConfirmRefresh(false)
          }}
          footer={
            <>
              <Button
                variant="ghost"
                disabled={refreshAddressesMut.isPending}
                onClick={() => setConfirmRefresh(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="secondary"
                disabled={refreshAddressesMut.isPending}
                onClick={() => refreshAddressesMut.mutate()}
              >
                {refreshAddressesMut.isPending
                  ? t('geo.positions.refreshing')
                  : t('geo.positions.refresh')}
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
            <p>
              {t('geo.positions.refreshModalP1pre')}
              <code>TEST</code>
              {t('geo.positions.refreshModalP1post')}
              <strong>{t('geo.positions.refreshModalP1notChanged')}</strong>
              {t('geo.positions.refreshModalP1tail')}
            </p>
            <p>{t('geo.positions.refreshModalP2')}</p>
            <p className="text-xs">{t('geo.positions.refreshModalP3')}</p>
          </div>
        </Modal>
      ) : null}

      {confirmRandomize && isAdmin ? (
        <Modal
          title={t('geo.positions.randomizeModalTitle')}
          onClose={() => {
            if (!randomizeMut.isPending) setConfirmRandomize(false)
          }}
          footer={
            <>
              <Button
                variant="ghost"
                disabled={randomizeMut.isPending}
                onClick={() => setConfirmRandomize(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="secondary"
                disabled={randomizeMut.isPending}
                onClick={() => randomizeMut.mutate()}
              >
                {randomizeMut.isPending ? t('geo.positions.setting') : t('geo.positions.set')}
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
            <p>
              {t('geo.positions.randomizeModalP1pre')}
              <code>TEST</code>
              {t('geo.positions.randomizeModalP1mid')}
            </p>
            <p className="text-xs">{t('geo.positions.randomizeModalP2')}</p>
          </div>
        </Modal>
      ) : null}

      {confirmRandomizeTimes && isAdmin ? (
        <Modal
          title={t('geo.positions.randomizeTimesModalTitle')}
          onClose={() => {
            if (!randomizeTimesMut.isPending) setConfirmRandomizeTimes(false)
          }}
          footer={
            <>
              <Button
                variant="ghost"
                disabled={randomizeTimesMut.isPending}
                onClick={() => setConfirmRandomizeTimes(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="secondary"
                disabled={randomizeTimesMut.isPending}
                onClick={() => randomizeTimesMut.mutate()}
              >
                {randomizeTimesMut.isPending
                  ? t('geo.positions.setting')
                  : t('geo.positions.set')}
              </Button>
            </>
          }
        >
          <div className="space-y-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
            <p>
              {t('geo.positions.randomizeTimesModalP1pre')}
              <code>TEST</code>
              {t('geo.positions.randomizeTimesModalP1mid')}
            </p>
            <p className="text-xs">{t('geo.positions.randomizeModalP2')}</p>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

function SortableTh({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string
  k: SortKey
  sortKey: SortKey
  sortDir: SortDir
  onClick: (k: SortKey) => void
}) {
  const active = sortKey === k
  return (
    <th
      className="cursor-pointer px-3 py-2 select-none hover:text-[color:var(--stryzh-color-text-primary)]"
      onClick={() => onClick(k)}
    >
      {label}
      {active ? (
        <span className="ml-1 inline-block">{sortDir === 'asc' ? '▲' : '▼'}</span>
      ) : null}
    </th>
  )
}

/**
 * Компактные индикаторы состояния трекера на устройстве инспектора:
 *  - режим (off/idle/active_order) — цветная точка с подписью;
 *  - permission — предупреждение, если НЕ granted;
 *  - очередь — чип «в очереди: N» если pending_buffer_size > 0;
 *  - ошибка — иконка ⚠️ с tooltip на last_geo_error.
 *
 * Если данных нет (старый клиент или не инспектор) — рисуем «—».
 */
function TrackerCell({ row }: { row: InspectorPositionRow }) {
  const hasAnySignal =
    row.tracking_mode != null ||
    row.permission_status != null ||
    row.pending_buffer_size > 0 ||
    !!row.last_geo_error ||
    row.last_position_accuracy_m != null
  if (!hasAnySignal) {
    return (
      <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">—</span>
    )
  }
  const updatedAt = row.tracking_status_updated_at
  return (
    <div className="flex flex-wrap items-center gap-1">
      <ModeDot mode={row.tracking_mode} updatedAt={updatedAt} />
      {row.permission_status ? (
        <PermissionBadge status={row.permission_status} updatedAt={updatedAt} />
      ) : null}
      {row.last_position_accuracy_m != null ? (
        <AccuracyBadge
          accuracyM={row.last_position_accuracy_m}
          updatedAt={row.last_position_at}
        />
      ) : null}
      {row.pending_buffer_size > 0 ? (
        <QueueChip count={row.pending_buffer_size} updatedAt={updatedAt} />
      ) : null}
      {row.last_geo_error ? (
        <ErrorIcon message={row.last_geo_error} updatedAt={updatedAt} />
      ) : null}
    </div>
  )
}

/** Бейдж точности последнего GPS-фикса. Пороги:
 *   <= 30м   — GPS точно (зелёный)
 *   <= 100м  — норм (приглушённый зелёный)
 *   <= 500м  — грубо (жёлтый, скорее всего сотовая вышка)
 *   >  500м  — приблизительно (красный, типично iOS «Precise Location» OFF —
 *              координаты снэпятся к ~1км сетке, трек смотрится как 2-3 точки)
 *
 *  При accuracy >= 1000м в tooltip явно указываем подозрение на Approximate
 *  Location iOS — это самая частая причина в проде. */
function AccuracyBadge({
  accuracyM,
  updatedAt,
}: {
  accuracyM: number
  updatedAt: string | null
}) {
  const a = Math.round(accuracyM)
  const conf =
    a <= 30
      ? {
          label: i18n.t('geo.accuracy.exact.label', { m: a }),
          cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
          tip: i18n.t('geo.accuracy.exact.tip'),
        }
      : a <= 100
      ? {
          label: i18n.t('geo.accuracy.good.label', { m: a }),
          cls: 'border-emerald-500/30 bg-emerald-500/8 text-emerald-200',
          tip: i18n.t('geo.accuracy.good.tip'),
        }
      : a <= 500
      ? {
          label: i18n.t('geo.accuracy.coarse.label', { m: a }),
          cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
          tip: i18n.t('geo.accuracy.coarse.tip'),
        }
      : {
          label: i18n.t('geo.accuracy.approx.label', { m: a }),
          cls: 'border-red-500/40 bg-red-500/10 text-red-300',
          tip:
            a >= 1000
              ? i18n.t('geo.accuracy.approx.tipIos')
              : i18n.t('geo.accuracy.approx.tipCell'),
        }
  return (
    <span
      className={`${TRACKER_PILL_BASE} ${conf.cls}`}
      title={withTime(conf.tip, updatedAt)}
    >
      {conf.label}
    </span>
  )
}

const TRACKER_PILL_BASE =
  'inline-flex items-center gap-1 rounded-[100px] border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.4px] font-medium'

/** Чип «Пульс» — определяется по last_keepalive_at и порогу grace, выбранному
 *  по текущему режиму инспектора (active_order vs idle/off). */
function ConnectionPill({
  row,
  graceSeconds,
}: {
  row: InspectorPositionRow
  graceSeconds: number
}) {
  const c = connection(row.last_keepalive_at, graceSeconds)
  const conf = CONN_BADGE[c]
  const tip = i18n.t(conf.tipKey)
  const tooltip = row.last_keepalive_at
    ? `${tip}\n${i18n.t('geo.conn.lastKeepalive', {
        time: formatDistanceToNow(new Date(row.last_keepalive_at), {
          locale: dateFnsLocale(),
          addSuffix: true,
        }),
      })}`
    : tip
  return (
    <span className={`${FRESH_PILL_BASE} ${conf.cls}`} title={tooltip}>
      {i18n.t(conf.labelKey)}
    </span>
  )
}

function ModeDot({
  mode,
  updatedAt,
}: {
  mode: InspectorPositionRow['tracking_mode']
  updatedAt: string | null
}) {
  if (mode == null) {
    return (
      <span
        className={`${TRACKER_PILL_BASE} border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]`}
        title={i18n.t('geo.mode.unknownTitle')}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        ?
      </span>
    )
  }
  const conf = {
    off: {
      cls: 'border-red-500/40 bg-red-500/10 text-red-300',
      label: 'OFF',
      tip: i18n.t('geo.mode.off.tip'),
    },
    idle: {
      cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
      label: 'IDLE',
      tip: i18n.t('geo.mode.idle.tip'),
    },
    active_order: {
      cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
      label: 'ACTIVE',
      tip: i18n.t('geo.mode.active.tip'),
    },
  }[mode]
  return (
    <span className={`${TRACKER_PILL_BASE} ${conf.cls}`} title={withTime(conf.tip, updatedAt)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {conf.label}
    </span>
  )
}

function PermissionBadge({
  status,
  updatedAt,
}: {
  status: NonNullable<InspectorPositionRow['permission_status']>
  updatedAt: string | null
}) {
  const conf: Record<
    typeof status,
    { label: string; tip: string; cls: string; prefix: string }
  > = {
    granted: {
      label: 'ALWAYS',
      tip: i18n.t('geo.perm.granted.tip'),
      cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
      prefix: '✓',
    },
    while_using: {
      label: 'WHILE-USE',
      tip: i18n.t('geo.perm.whileUsing.tip'),
      cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
      prefix: '⚠',
    },
    denied: {
      label: 'DENIED',
      tip: i18n.t('geo.perm.denied.tip'),
      cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
      prefix: '⚠',
    },
    denied_forever: {
      label: 'DENIED ⨯',
      tip: i18n.t('geo.perm.deniedForever.tip'),
      cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
      prefix: '⚠',
    },
    restricted: {
      label: 'RESTRICTED',
      tip: i18n.t('geo.perm.restricted.tip'),
      cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
      prefix: '⚠',
    },
    unknown: {
      label: '?',
      tip: i18n.t('geo.perm.unknown.tip'),
      cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
      prefix: '⚠',
    },
  }
  const c = conf[status]
  return (
    <span className={`${TRACKER_PILL_BASE} ${c.cls}`} title={withTime(c.tip, updatedAt)}>
      {c.prefix} {c.label}
    </span>
  )
}

function QueueChip({ count, updatedAt }: { count: number; updatedAt: string | null }) {
  return (
    <span
      className={`${TRACKER_PILL_BASE} border-amber-500/40 bg-amber-500/10 text-amber-300`}
      title={withTime(i18n.t('geo.queue.tip', { count }), updatedAt)}
    >
      ⏳ {count}
    </span>
  )
}

function ErrorIcon({ message, updatedAt }: { message: string; updatedAt: string | null }) {
  return (
    <span
      className={`${TRACKER_PILL_BASE} border-red-500/40 bg-red-500/10 text-red-300`}
      title={withTime(i18n.t('geo.error.tip', { message }), updatedAt)}
    >
      ⚠ ERR
    </span>
  )
}

function IconBtn({
  title,
  onClick,
  children,
}: {
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className="rounded px-2 py-1 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/10 hover:text-[color:var(--stryzh-color-text-primary)]"
    >
      {children}
    </button>
  )
}
